
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import axiosClient from "../../../../api/axiosClient";
import tradingActualDispatchService from "../services/tradingActualDispatchService";

const warehouseApi = "/api/ppe/material-flow/warehouse/master/list";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  padding: "20px",
  marginBottom: "18px",
};

const sectionTitleStyle = {
  margin: "0 0 18px",
  fontSize: "16px",
  fontWeight: 700,
  color: "#1f2937",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "#475569",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  padding: "9px 11px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "13px",
  color: "#1f2937",
  background: "#fff",
  boxSizing: "border-box",
};

const tableHeaderStyle = {
  padding: "11px 10px",
  textAlign: "left",
  fontSize: "11px",
  fontWeight: 700,
  color: "#475569",
  background: "#f8fafc",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  padding: "11px 10px",
  fontSize: "12px",
  color: "#374151",
  borderBottom: "1px solid #edf0f4",
  verticalAlign: "middle",
};

const primaryButtonStyle = {
  padding: "10px 19px",
  border: "none",
  borderRadius: "6px",
  background: "#5145cd",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  padding: "10px 16px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  background: "#fff",
  color: "#374151",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
};

const formatQuantity = (value) => {
  const number = Number(value || 0);

  return number.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });
};

const formatDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const getResponseData = (response) => {
  return response?.data?.data ?? response?.data ?? {};
};

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};

const getWarehouseId = (warehouse) =>
  warehouse?.id ?? warehouse?.warehouse_id;

const getWarehouseName = (warehouse) =>
  warehouse?.warehouse_name ||
  warehouse?.warehouse_code ||
  warehouse?.name ||
  warehouse?.warehouse_code_name ||
  `Warehouse ${getWarehouseId(warehouse)}`;

const ActualDispatchEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [actualDispatch, setActualDispatch] = useState(null);
  const [dispatchItems, setDispatchItems] = useState([]);

  const [warehouses, setWarehouses] = useState([]);
  const [transporters, setTransporters] = useState([]);

  const [actualDispatchDate, setActualDispatchDate] = useState("");
  const [transporterBpId, setTransporterBpId] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverMobile, setDriverMobile] = useState("");
  const [lrNumber, setLrNumber] = useState("");
  const [lrDate, setLrDate] = useState("");
  const [remarks, setRemarks] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingTransporters, setLoadingTransporters] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // ---------------------------------------------------------
  // LOAD EXISTING ACTUAL DISPATCH
  // ---------------------------------------------------------

  const loadActualDispatch = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setInfo("");

      const response =
        await tradingActualDispatchService.getActualDispatchDetail(id);

      const data = getResponseData(response);

      const record =
        data?.actual_dispatch ||
        data?.record ||
        data;

      if (!record || !record.id) {
        throw new Error("Actual Dispatch record could not be loaded.");
      }

      setActualDispatch(record);

      if (String(record.status_code || "").toUpperCase() !== "DRAFT") {
        setError(
          "Only Draft Actual Dispatch records can be edited. This record is read-only."
        );
        return;
      }

      setActualDispatchDate(
        formatDate(record.actual_dispatch_date)
      );

      setTransporterBpId(
        record.transporter_bp_id
          ? String(record.transporter_bp_id)
          : ""
      );

      setVehicleNumber(record.vehicle_number || "");
      setDriverName(record.driver_name || "");
      setDriverMobile(record.driver_mobile || "");
      setLrNumber(record.lr_number || "");
      setLrDate(formatDate(record.lr_date));
      setRemarks(record.remarks || "");

      const rows = (record.items || []).map((item) => {
        const allocations =
          item.batch_allocations ||
          item.batches ||
          [];

        return {
          ...item,

          dispatch_order_item_id:
            item.dispatch_order_item_id,

          actual_dispatch_quantity:
            item.actual_dispatch_quantity ?? "",

          warehouse_id: item.warehouse_id
            ? String(item.warehouse_id)
            : "",

          batch_allocations: allocations.map((allocation) => ({
            id: allocation.id,
            batch_no: allocation.batch_no || "",
            dispatch_quantity:
              allocation.dispatch_quantity ?? "",
          })),

          showBatchAllocation: allocations.length > 0,

          planned_dispatch_quantity:
            item.planned_quantity_snapshot ??
            item.planned_dispatch_quantity ??
            0,

          confirmed_dispatch_quantity:
            item.previously_dispatched_snapshot ??
            item.confirmed_dispatch_quantity ??
            0,
        };
      });

      setDispatchItems(rows);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // ---------------------------------------------------------
  // LOAD WAREHOUSES
  // ---------------------------------------------------------

  const loadWarehouses = useCallback(async () => {
    try {
      setLoadingWarehouses(true);

      const response = await axiosClient.get(warehouseApi);
      const data = getResponseData(response);

      const rows = Array.isArray(data)
        ? data
        : data?.items || data?.warehouses || [];

      setWarehouses(
        rows.filter(
          (warehouse) =>
            warehouse.is_active === undefined ||
            warehouse.is_active === true ||
            warehouse.is_active === 1
        )
      );
    } catch (err) {
      setError(
        `Unable to load warehouses. ${getErrorMessage(err)}`
      );
    } finally {
      setLoadingWarehouses(false);
    }
  }, []);

  // ---------------------------------------------------------
  // LOAD TRANSPORTERS
  // ---------------------------------------------------------

  const loadTransporters = useCallback(async () => {
    try {
      setLoadingTransporters(true);

      const response = await axiosClient.get(
        "/api/ppe/transport/transporters/search"
      );

      const data = getResponseData(response);

      const rows = Array.isArray(data)
        ? data
        : data?.items || data?.transporters || [];

      setTransporters(rows);
    } catch (err) {
      console.error("Unable to load transporters:", err);
    } finally {
      setLoadingTransporters(false);
    }
  }, []);

  useEffect(() => {
    loadActualDispatch();
    loadWarehouses();
    loadTransporters();
  }, [loadActualDispatch, loadWarehouses, loadTransporters]);

  // ---------------------------------------------------------
  // ITEM QUANTITY CHANGE
  // ---------------------------------------------------------

  const handleQuantityChange = (itemId, value) => {
    setDispatchItems((previous) =>
      previous.map((item) => {
        if (
          String(item.dispatch_order_item_id) !== String(itemId)
        ) {
          return item;
        }

        return {
          ...item,
          actual_dispatch_quantity: value,
          batch_allocations: [],
        };
      })
    );
  };

  // ---------------------------------------------------------
  // WAREHOUSE CHANGE
  // ---------------------------------------------------------

  const handleWarehouseChange = (itemId, value) => {
    setDispatchItems((previous) =>
      previous.map((item) =>
        String(item.dispatch_order_item_id) === String(itemId)
          ? {
              ...item,
              warehouse_id: value,
            }
          : item
      )
    );
  };

  // ---------------------------------------------------------
  // BATCH ALLOCATION
  // ---------------------------------------------------------

  const toggleBatchAllocation = (itemId) => {
    setDispatchItems((previous) =>
      previous.map((item) =>
        String(item.dispatch_order_item_id) === String(itemId)
          ? {
              ...item,
              showBatchAllocation: !item.showBatchAllocation,
            }
          : item
      )
    );
  };

  const addBatchAllocation = (itemId) => {
    setDispatchItems((previous) =>
      previous.map((item) => {
        if (
          String(item.dispatch_order_item_id) !== String(itemId)
        ) {
          return item;
        }

        return {
          ...item,
          batch_allocations: [
            ...item.batch_allocations,
            {
              batch_no: "",
              dispatch_quantity: "",
            },
          ],
          showBatchAllocation: true,
        };
      })
    );
  };

  const updateBatchAllocation = (
    itemId,
    allocationIndex,
    field,
    value
  ) => {
    setDispatchItems((previous) =>
      previous.map((item) => {
        if (
          String(item.dispatch_order_item_id) !== String(itemId)
        ) {
          return item;
        }

        return {
          ...item,
          batch_allocations: item.batch_allocations.map(
            (allocation, index) =>
              index === allocationIndex
                ? {
                    ...allocation,
                    [field]: value,
                  }
                : allocation
          ),
        };
      })
    );
  };

  const removeBatchAllocation = (itemId, allocationIndex) => {
    setDispatchItems((previous) =>
      previous.map((item) => {
        if (
          String(item.dispatch_order_item_id) !== String(itemId)
        ) {
          return item;
        }

        return {
          ...item,
          batch_allocations: item.batch_allocations.filter(
            (_, index) => index !== allocationIndex
          ),
        };
      })
    );
  };

  // ---------------------------------------------------------
  // TOTALS
  // ---------------------------------------------------------

  const totalDispatchQuantity = useMemo(
    () =>
      dispatchItems.reduce(
        (total, item) =>
          total + Number(item.actual_dispatch_quantity || 0),
        0
      ),
    [dispatchItems]
  );

  const selectedItemCount = dispatchItems.filter(
    (item) => Number(item.actual_dispatch_quantity || 0) > 0
  ).length;

  // ---------------------------------------------------------
  // VALIDATION
  // ---------------------------------------------------------

  const validateForm = () => {
    if (!actualDispatchDate) {
      return "Actual Dispatch Date is required.";
    }

    const selectedItems = dispatchItems.filter(
      (item) => Number(item.actual_dispatch_quantity || 0) > 0
    );

    if (!selectedItems.length) {
      return "Enter an actual dispatch quantity for at least one item.";
    }

    for (const item of selectedItems) {
      const quantity = Number(item.actual_dispatch_quantity);

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return `Enter a valid quantity for ${item.item_description}.`;
      }

      if (!item.warehouse_id) {
        return `Please select a warehouse for ${item.item_description}.`;
      }

      const allocations = item.batch_allocations || [];

      if (allocations.length) {
        let allocatedTotal = 0;
        const seenBatches = new Set();

        for (const allocation of allocations) {
          const batchNo = String(
            allocation.batch_no || ""
          ).trim();

          const batchQty = Number(
            allocation.dispatch_quantity
          );

          if (!batchNo) {
            return `Enter a batch number for ${item.item_description}.`;
          }

          if (!Number.isFinite(batchQty) || batchQty <= 0) {
            return `Enter a valid batch quantity for ${item.item_description}.`;
          }

          if (seenBatches.has(batchNo)) {
            return `Batch ${batchNo} is entered more than once for ${item.item_description}.`;
          }

          seenBatches.add(batchNo);
          allocatedTotal += batchQty;
        }

        if (Math.abs(allocatedTotal - quantity) > 0.000001) {
          return `Batch allocation total for ${item.item_description} must equal the actual dispatch quantity.`;
        }
      }
    }

    return "";
  };

  // ---------------------------------------------------------
  // SAVE UPDATED DRAFT
  // ---------------------------------------------------------

  const handleSave = async () => {
    if (
      !actualDispatch ||
      String(actualDispatch.status_code || "").toUpperCase() !== "DRAFT"
    ) {
      setError("Only Draft Actual Dispatch records can be edited.");
      return;
    }

    const validationMessage = validateForm();

    if (validationMessage) {
      setError(validationMessage);
      setInfo("");
      return;
    }

    const selectedItems = dispatchItems.filter(
      (item) => Number(item.actual_dispatch_quantity || 0) > 0
    );

    const payload = {
      actual_dispatch_date: actualDispatchDate,

      transporter_bp_id: transporterBpId
        ? Number(transporterBpId)
        : null,

      vehicle_number: vehicleNumber.trim() || null,
      driver_name: driverName.trim() || null,
      driver_mobile: driverMobile.trim() || null,
      lr_number: lrNumber.trim() || null,
      lr_date: lrDate || null,
      remarks: remarks.trim() || null,

      items: selectedItems.map((item) => ({
        dispatch_order_item_id: Number(
          item.dispatch_order_item_id
        ),

        actual_dispatch_quantity: Number(
          item.actual_dispatch_quantity
        ),

        warehouse_id: Number(item.warehouse_id),

        batch_allocations: (
          item.batch_allocations || []
        ).map((allocation) => ({
          batch_no: allocation.batch_no.trim(),
          dispatch_quantity: Number(
            allocation.dispatch_quantity
          ),
        })),
      })),
    };

    try {
      setSaving(true);
      setError("");
      setInfo("");

      await tradingActualDispatchService.updateActualDispatch(
        id,
        payload
      );

      setInfo(
        `Actual Dispatch ${actualDispatch.actual_dispatch_no || ""} updated successfully.`
      );

      navigate(`/trading/actual-dispatches/${id}`, {
        state: {
          updated: true,
        },
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------

  if (loading) {
    return (
      <div
        style={{
          padding: "24px",
          minHeight: "100%",
          background: "#f8fafc",
          color: "#64748b",
          fontSize: "13px",
        }}
      >
        Loading Actual Dispatch...
      </div>
    );
  }

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------

  const isDraft =
    String(actualDispatch?.status_code || "").toUpperCase() ===
    "DRAFT";

  return (
    <div
      style={{
        padding: "24px",
        minHeight: "100%",
        background: "#f8fafc",
      }}
    >
      {/* PAGE HEADER */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "22px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              marginBottom: "6px",
            }}
          >
            Trading / Actual Dispatch / Edit
          </div>

          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Edit Actual Dispatch
          </h2>

          <p
            style={{
              margin: "7px 0 0",
              fontSize: "13px",
              color: "#64748b",
            }}
          >
            Update the draft shipment details, quantities, warehouses
            and batch allocations.
          </p>
        </div>

        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() =>
            navigate(`/trading/actual-dispatches/${id}`)
          }
          disabled={saving}
        >
          Back to Actual Dispatch
        </button>
      </div>

      {/* ALERTS */}

      {error && (
        <div
          role="alert"
          style={{
            padding: "12px 15px",
            borderRadius: "8px",
            marginBottom: "16px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      {info && (
        <div
          role="status"
          style={{
            padding: "12px 15px",
            borderRadius: "8px",
            marginBottom: "16px",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4ed8",
            fontSize: "13px",
          }}
        >
          {info}
        </div>
      )}

      {!actualDispatch ? (
        <section style={cardStyle}>
          Actual Dispatch record not found.
        </section>
      ) : !isDraft ? (
        <section style={cardStyle}>
          <strong>Editing is not available.</strong>
          <p style={{ fontSize: "13px", color: "#64748b" }}>
            Only Draft Actual Dispatch records can be edited.
            Current status: {actualDispatch.status_code || "-"}
          </p>
        </section>
      ) : (
        <>
          {/* SECTION 1: DISPATCH ORDER DETAILS */}

          <section style={cardStyle}>
            <h3 style={sectionTitleStyle}>
              1. Dispatch Order Details
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <div style={labelStyle}>Actual Dispatch Number</div>
                <strong>
                  {actualDispatch.actual_dispatch_no || "-"}
                </strong>
              </div>

              <div>
                <div style={labelStyle}>Dispatch Order Number</div>
                <strong>
                  {actualDispatch.dispatch_order_no ||
                    actualDispatch.dispatch_order?.dispatch_order_no ||
                    "-"}
                </strong>
              </div>

              <div>
                <div style={labelStyle}>Sales Order Number</div>
                <strong>
                  {actualDispatch.sales_order_no || "-"}
                </strong>
              </div>

              <div>
                <div style={labelStyle}>Customer</div>
                <strong>
                  {actualDispatch.customer_name || "-"}
                </strong>
              </div>

              <div>
                <div style={labelStyle}>Customer ID</div>
                <strong>
                  {actualDispatch.customer_id || "-"}
                </strong>
              </div>

              <div>
                <div style={labelStyle}>Status</div>
                <span
                  style={{
                    display: "inline-block",
                    padding: "4px 8px",
                    borderRadius: "5px",
                    background: "#fef3c7",
                    color: "#92400e",
                    fontSize: "11px",
                    fontWeight: 700,
                  }}
                >
                  DRAFT
                </span>
              </div>
            </div>

            <div style={{ marginTop: "18px" }}>
              <div style={labelStyle}>Delivery Address</div>
              <div style={{ fontSize: "13px", color: "#374151" }}>
                {actualDispatch.delivery_address || "-"}
              </div>
            </div>
          </section>

          {/* SECTION 2: ACTUAL DISPATCH ITEMS */}

          <section style={cardStyle}>
            <h3 style={sectionTitleStyle}>
              2. Actual Dispatch Items
            </h3>

            {dispatchItems.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: "13px" }}>
                No items found in this Actual Dispatch.
              </div>
            ) : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      minWidth: "1050px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={tableHeaderStyle}>Line</th>
                        <th style={tableHeaderStyle}>
                          Item Description
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            textAlign: "right",
                          }}
                        >
                          Planned Qty
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            textAlign: "right",
                          }}
                        >
                          Previously Dispatched
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            minWidth: "140px",
                          }}
                        >
                          Actual Dispatch Qty
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            minWidth: "180px",
                          }}
                        >
                          Warehouse
                        </th>
                        <th style={tableHeaderStyle}>
                          Batch Allocation
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {dispatchItems.map((item) => {
                        const quantity = Number(
                          item.actual_dispatch_quantity || 0
                        );

                        const allocationTotal = (
                          item.batch_allocations || []
                        ).reduce(
                          (sum, allocation) =>
                            sum +
                            Number(
                              allocation.dispatch_quantity || 0
                            ),
                          0
                        );

                        return (
                          <React.Fragment
                            key={item.dispatch_order_item_id}
                          >
                            <tr>
                              <td style={tableCellStyle}>
                                {item.item_seq_no ?? "-"}
                              </td>

                              <td style={tableCellStyle}>
                                <div style={{ fontWeight: 600 }}>
                                  {item.item_description || "-"}
                                </div>

                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#64748b",
                                    marginTop: "3px",
                                  }}
                                >
                                  Material ID:{" "}
                                  {item.raw_material_id || "-"}
                                </div>
                              </td>

                              <td
                                style={{
                                  ...tableCellStyle,
                                  textAlign: "right",
                                }}
                              >
                                {formatQuantity(
                                  item.planned_dispatch_quantity
                                )}
                              </td>

                              <td
                                style={{
                                  ...tableCellStyle,
                                  textAlign: "right",
                                }}
                              >
                                {formatQuantity(
                                  item.confirmed_dispatch_quantity
                                )}
                              </td>

                              <td style={tableCellStyle}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.001"
                                  value={item.actual_dispatch_quantity}
                                  onChange={(event) =>
                                    handleQuantityChange(
                                      item.dispatch_order_item_id,
                                      event.target.value
                                    )
                                  }
                                  style={inputStyle}
                                />
                              </td>

                              <td style={tableCellStyle}>
                                <select
                                  value={item.warehouse_id}
                                  onChange={(event) =>
                                    handleWarehouseChange(
                                      item.dispatch_order_item_id,
                                      event.target.value
                                    )
                                  }
                                  disabled={loadingWarehouses}
                                  style={inputStyle}
                                >
                                  <option value="">
                                    {loadingWarehouses
                                      ? "Loading warehouses..."
                                      : "Select Warehouse"}
                                  </option>

                                  {warehouses.map((warehouse) => (
                                    <option
                                      key={getWarehouseId(warehouse)}
                                      value={getWarehouseId(warehouse)}
                                    >
                                      {getWarehouseName(warehouse)}
                                    </option>
                                  ))}
                                </select>
                              </td>

                              <td style={tableCellStyle}>
                                <button
                                  type="button"
                                  style={{
                                    ...secondaryButtonStyle,
                                    padding: "6px 9px",
                                    fontSize: "11px",
                                  }}
                                  disabled={quantity <= 0}
                                  onClick={() =>
                                    toggleBatchAllocation(
                                      item.dispatch_order_item_id
                                    )
                                  }
                                >
                                  {item.showBatchAllocation
                                    ? "Hide Batches"
                                    : `Batches (${item.batch_allocations.length})`}
                                </button>
                              </td>
                            </tr>

                            {item.showBatchAllocation && (
                              <tr>
                                <td
                                  colSpan={7}
                                  style={{
                                    padding: "14px",
                                    background: "#f8fafc",
                                    borderBottom:
                                      "1px solid #e5e7eb",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      gap: "12px",
                                      flexWrap: "wrap",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    <div>
                                      <strong
                                        style={{
                                          fontSize: "13px",
                                          color: "#1f2937",
                                        }}
                                      >
                                        Batch Allocation
                                      </strong>

                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#64748b",
                                          marginTop: "4px",
                                        }}
                                      >
                                        Actual quantity:{" "}
                                        {formatQuantity(quantity)}
                                        {" | "}
                                        Allocated:{" "}
                                        {formatQuantity(allocationTotal)}
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      style={{
                                        ...primaryButtonStyle,
                                        padding: "7px 12px",
                                        fontSize: "11px",
                                      }}
                                      onClick={() =>
                                        addBatchAllocation(
                                          item.dispatch_order_item_id
                                        )
                                      }
                                    >
                                      + Add Batch
                                    </button>
                                  </div>

                                  {item.batch_allocations.length ===
                                  0 ? (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#64748b",
                                      }}
                                    >
                                      No batch allocations entered.
                                      Batch allocation will be validated
                                      against Inventory Configuration
                                      when saving.
                                    </div>
                                  ) : (
                                    <div
                                      style={{ overflowX: "auto" }}
                                    >
                                      <table
                                        style={{
                                          width: "100%",
                                          maxWidth: "700px",
                                          borderCollapse: "collapse",
                                        }}
                                      >
                                        <thead>
                                          <tr>
                                            <th style={tableHeaderStyle}>
                                              Batch Number
                                            </th>
                                            <th style={tableHeaderStyle}>
                                              Dispatch Quantity
                                            </th>
                                            <th style={tableHeaderStyle}>
                                              Action
                                            </th>
                                          </tr>
                                        </thead>

                                        <tbody>
                                          {item.batch_allocations.map(
                                            (allocation, index) => (
                                              <tr key={index}>
                                                <td
                                                  style={tableCellStyle}
                                                >
                                                  <input
                                                    type="text"
                                                    value={
                                                      allocation.batch_no
                                                    }
                                                    placeholder="Enter batch number"
                                                    onChange={(event) =>
                                                      updateBatchAllocation(
                                                        item.dispatch_order_item_id,
                                                        index,
                                                        "batch_no",
                                                        event.target.value
                                                      )
                                                    }
                                                    style={{
                                                      ...inputStyle,
                                                      maxWidth: "260px",
                                                    }}
                                                  />
                                                </td>

                                                <td
                                                  style={tableCellStyle}
                                                >
                                                  <input
                                                    type="number"
                                                    min="0"
                                                    step="0.001"
                                                    value={
                                                      allocation.dispatch_quantity
                                                    }
                                                    placeholder="Quantity"
                                                    onChange={(event) =>
                                                      updateBatchAllocation(
                                                        item.dispatch_order_item_id,
                                                        index,
                                                        "dispatch_quantity",
                                                        event.target.value
                                                      )
                                                    }
                                                    style={{
                                                      ...inputStyle,
                                                      maxWidth: "180px",
                                                    }}
                                                  />
                                                </td>

                                                <td
                                                  style={tableCellStyle}
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      removeBatchAllocation(
                                                        item.dispatch_order_item_id,
                                                        index
                                                      )
                                                    }
                                                    style={{
                                                      ...secondaryButtonStyle,
                                                      color: "#b91c1c",
                                                      padding: "6px 10px",
                                                    }}
                                                  >
                                                    Remove
                                                  </button>
                                                </td>
                                              </tr>
                                            )
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  <div
                                    style={{
                                      marginTop: "10px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color:
                                        item.batch_allocations.length >
                                          0 &&
                                        Math.abs(
                                          allocationTotal - quantity
                                        ) > 0.000001
                                          ? "#b91c1c"
                                          : "#166534",
                                    }}
                                  >
                                    Allocated:{" "}
                                    {formatQuantity(allocationTotal)}
                                    {" / "}
                                    {formatQuantity(quantity)}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "24px",
                    flexWrap: "wrap",
                    marginTop: "16px",
                    padding: "13px 15px",
                    borderRadius: "7px",
                    background: "#f8fafc",
                    fontSize: "13px",
                  }}
                >
                  <div>
                    <span style={{ color: "#64748b" }}>
                      Selected Items:{" "}
                    </span>
                    <strong>{selectedItemCount}</strong>
                  </div>

                  <div>
                    <span style={{ color: "#64748b" }}>
                      Total Actual Dispatch Quantity:{" "}
                    </span>
                    <strong>
                      {formatQuantity(totalDispatchQuantity)}
                    </strong>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* SECTION 3: TRANSPORT DETAILS */}

          <section style={cardStyle}>
            <h3 style={sectionTitleStyle}>
              3. Actual Dispatch and Transport Details
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <label
                  style={labelStyle}
                  htmlFor="actual-dispatch-date"
                >
                  Actual Dispatch Date *
                </label>

                <input
                  id="actual-dispatch-date"
                  type="date"
                  value={actualDispatchDate}
                  onChange={(event) =>
                    setActualDispatchDate(event.target.value)
                  }
                  style={inputStyle}
                />
              </div>

              <div>
                <label
                  style={labelStyle}
                  htmlFor="actual-transporter"
                >
                  Transporter
                </label>

                <select
                  id="actual-transporter"
                  value={transporterBpId}
                  onChange={(event) =>
                    setTransporterBpId(event.target.value)
                  }
                  disabled={loadingTransporters}
                  style={inputStyle}
                >
                  <option value="">
                    {loadingTransporters
                      ? "Loading transporters..."
                      : "Select Transporter"}
                  </option>

                  {transporters.map((transporter) => (
                    <option
                      key={transporter.id}
                      value={transporter.id}
                    >
                      {transporter.name ||
                        transporter.bp_name ||
                        transporter.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  style={labelStyle}
                  htmlFor="vehicle-number"
                >
                  Vehicle Number
                </label>

                <input
                  id="vehicle-number"
                  type="text"
                  value={vehicleNumber}
                  onChange={(event) =>
                    setVehicleNumber(event.target.value)
                  }
                  style={inputStyle}
                  placeholder="Enter vehicle number"
                />
              </div>

              <div>
                <label style={labelStyle} htmlFor="driver-name">
                  Driver Name
                </label>

                <input
                  id="driver-name"
                  type="text"
                  value={driverName}
                  onChange={(event) =>
                    setDriverName(event.target.value)
                  }
                  style={inputStyle}
                  placeholder="Enter driver name"
                />
              </div>

              <div>
                <label
                  style={labelStyle}
                  htmlFor="driver-mobile"
                >
                  Driver Mobile
                </label>

                <input
                  id="driver-mobile"
                  type="text"
                  value={driverMobile}
                  onChange={(event) =>
                    setDriverMobile(event.target.value)
                  }
                  style={inputStyle}
                  placeholder="Enter driver mobile"
                />
              </div>

              <div>
                <label style={labelStyle} htmlFor="lr-number">
                  LR / Docket Number
                </label>

                <input
                  id="lr-number"
                  type="text"
                  value={lrNumber}
                  onChange={(event) =>
                    setLrNumber(event.target.value)
                  }
                  style={inputStyle}
                  placeholder="Enter LR / docket number"
                />
              </div>

              <div>
                <label style={labelStyle} htmlFor="lr-date">
                  LR Date
                </label>

                <input
                  id="lr-date"
                  type="date"
                  value={lrDate}
                  onChange={(event) =>
                    setLrDate(event.target.value)
                  }
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "18px" }}>
              <label
                style={labelStyle}
                htmlFor="actual-dispatch-remarks"
              >
                Remarks
              </label>

              <textarea
                id="actual-dispatch-remarks"
                rows={3}
                value={remarks}
                onChange={(event) =>
                  setRemarks(event.target.value)
                }
                style={{
                  ...inputStyle,
                  resize: "vertical",
                }}
                placeholder="Enter dispatch remarks"
              />
            </div>
          </section>

          {/* ACTIONS */}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              flexWrap: "wrap",
              paddingBottom: "24px",
            }}
          >
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={() =>
                navigate(`/trading/actual-dispatches/${id}`)
              }
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="button"
              style={{
                ...primaryButtonStyle,
                opacity: saving ? 0.7 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
              onClick={handleSave}
              disabled={
                saving ||
                loadingWarehouses ||
                !dispatchItems.length
              }
            >
              {saving ? "Updating..." : "Update Actual Dispatch Draft"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ActualDispatchEditPage;
